// src/components/Auth/AuthPromptDialog.js
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Input } from "../../ui/Input";
import { Button } from "../../ui/Button";
import { useAuth } from "../../context/AuthContext";
import { useAuthPrompt } from "../../context/AuthPromptContext";
import { getHomeRouteForRole } from "../../Utils/homeRoute";
import { backdrop, overlayPanel, useMotion } from "../../ui/motion";
import * as S from "./AuthPromptDialog.styled";

const AuthPromptDialog = () => {
  const { isOpen, promptConfig, closeAuthPrompt, completeAuthPrompt } = useAuthPrompt();
  const { login } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [credentials, setCredentials] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const dialogRef = useRef(null);
  const { reduce } = useMotion();

  useEffect(() => {
    if (!isOpen) return;
    setCredentials({ email: "", password: "" });
    setError("");
    setSubmitting(false);
    requestAnimationFrame(() => dialogRef.current?.focus());
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKey = (event) => {
      if (event.key === "Escape") {
        closeAuthPrompt();
      }
    };

    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, closeAuthPrompt]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setCredentials((prev) => ({ ...prev, [name]: value }));
  };

  /**
   * Send them to sign up, carrying anything they already typed and where they came from, so the
   * signup page can bring them back rather than dumping them on the home page.
   */
  const handleSignup = () => {
    const params = new URLSearchParams();
    const email = (credentials.email || "").trim();
    if (email) params.set("email", email);
    params.set("returnTo", `${window.location.pathname}${window.location.search}`);
    // This prompt only ever interrupts shopping (saving a farm, a wishlist, a
    // review), so the farm-or-shopper question is already answered — asking it
    // here would just stand between them and what they were doing.
    params.set("role", "client");

    closeAuthPrompt();
    navigate(`/signup?${params.toString()}`);
  };

  const getErrorMessage = (err) => {
    const data = err?.response?.data;
    if (typeof data?.message === "string" && data.message.trim()) return data.message;
    if (typeof data === "string" && data.trim()) return data;
    return t("login.error", { defaultValue: "Login failed. Please check your credentials." });
  };

  const requiresEmailConfirmation = (err, message) => {
    const code = err?.response?.data?.code;
    if (code === "EMAIL_CONFIRMATION_REQUIRED") return true;
    return /confirm your email/i.test(message || "");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSubmitting(true);

    try {
      const { role } = await login(credentials);

      if (role === "SuperAdmin" || role === "Villager") {
        closeAuthPrompt();
        navigate(getHomeRouteForRole(role));
        return;
      }

      completeAuthPrompt();
    } catch (err) {
      const message = getErrorMessage(err);

      if (requiresEmailConfirmation(err, message)) {
        closeAuthPrompt();
        const email = encodeURIComponent((credentials.email || "").trim());
        navigate(`/confirm-email${email ? `?email=${email}` : ""}`);
      } else {
        setError(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return ReactDOM.createPortal(
    <AnimatePresence>
      {isOpen ? (
        <S.Overlay
          as={motion.div}
          variants={backdrop(reduce)}
          initial="hidden"
          animate="show"
          exit="exit"
          onClick={closeAuthPrompt}
        >
          <S.DialogWrapper>
            <S.Dialog
              as={motion.div}
              ref={dialogRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby="auth-prompt-title"
              aria-describedby={promptConfig.body ? "auth-prompt-body" : undefined}
              tabIndex={-1}
              variants={overlayPanel(reduce)}
              initial="hidden"
              animate="show"
              exit="exit"
              onClick={(event) => event.stopPropagation()}
            >
          <S.CloseButton
            type="button"
            onClick={closeAuthPrompt}
            aria-label={t("common.close", { defaultValue: "Close" })}
          >
            &times;
          </S.CloseButton>

          <S.Header>
            <S.Title id="auth-prompt-title">{promptConfig.title}</S.Title>
          </S.Header>

          {promptConfig.body ? (
            <S.Body id="auth-prompt-body">{promptConfig.body}</S.Body>
          ) : null}

          <S.Form onSubmit={handleSubmit}>
            <Input
              id="auth-prompt-email"
              name="email"
              type="email"
              label={t("login.email", { defaultValue: "Email" })}
              placeholder={t("login.email_placeholder", { defaultValue: "you@example.com" })}
              value={credentials.email}
              onChange={handleChange}
              required
              autoComplete="email"
            />
            <Input
              id="auth-prompt-password"
              name="password"
              type="password"
              label={t("login.password", { defaultValue: "Password" })}
              placeholder={t("login.password_placeholder", { defaultValue: "Your password" })}
              value={credentials.password}
              onChange={handleChange}
              required
              autoComplete="current-password"
            />

            {error ? <S.ErrorMessage role="alert">{error}</S.ErrorMessage> : null}

            <S.Actions>
              <Button type="button" variant="ghost" onClick={closeAuthPrompt}>
                {t("common.cancel", { defaultValue: "Cancel" })}
              </Button>
              <Button type="submit" variant="primary" disabled={submitting}>
                {submitting
                  ? t("login.submitting", { defaultValue: "Logging in…" })
                  : promptConfig.submitLabel ||
                    t("login.submit", { defaultValue: "Log In" })}
              </Button>
            </S.Actions>
          </S.Form>

          {/* The copy invites you to "create an account or log in", but there was no way to do the
              first half. Anyone without an account had nothing to click. */}
          <S.AltAction>
            <span>
              {t("auth_prompt.no_account", { defaultValue: "Don't have an account yet?" })}
            </span>
            <S.AltLink type="button" onClick={handleSignup}>
              {t("auth_prompt.signup", { defaultValue: "Create one" })}
            </S.AltLink>
          </S.AltAction>
            </S.Dialog>
          </S.DialogWrapper>
        </S.Overlay>
      ) : null}
    </AnimatePresence>,
    document.body
  );
};

export default AuthPromptDialog;
